import { createContext, useState, useEffect, useCallback } from 'react';
import { ROLE_DASHBOARDS } from '../utils/constants';
import { getMe } from '../services/authService';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('trainaxis_token');
    const savedUser  = localStorage.getItem('trainaxis_user');
    if (savedToken && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setToken(savedToken);
        setUser(parsed);
        // Restore club slug so api.js header is populated on refresh
        if (parsed.clubSlug) {
          localStorage.setItem('trainaxis_club_slug', parsed.clubSlug);
        }
        // Refresh from /me in the background so cached fields that can go stale
        // (dashboardTheme, adminType / isPrimaryAdmin, clubSlug) reflect the server
        // — e.g. after a superadmin Primary-Admin transfer. We render immediately
        // from cache (no extra loading state) and merge the fresh data when it
        // arrives. An invalid/expired token surfaces as a 401, which the global
        // interceptor in api.js already handles (clears auth + redirects to login).
        getMe()
          .then(({ data }) => {
            if (!data?.user) return;
            setUser(prev => {
              const next = { ...prev, ...data.user };
              localStorage.setItem('trainaxis_user', JSON.stringify(next));
              if (next.clubSlug) {
                localStorage.setItem('trainaxis_club_slug', next.clubSlug);
              }
              return next;
            });
          })
          .catch(() => { /* network/5xx: keep cached user; 401 handled by interceptor */ });
      } catch {
        localStorage.removeItem('trainaxis_token');
        localStorage.removeItem('trainaxis_user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((userData, tokenData) => {
    setUser(userData);
    setToken(tokenData);
    localStorage.setItem('trainaxis_token', tokenData);
    localStorage.setItem('trainaxis_user', JSON.stringify(userData));
    if (userData.clubSlug) {
      localStorage.setItem('trainaxis_club_slug', userData.clubSlug);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('trainaxis_token');
    localStorage.removeItem('trainaxis_user');
    localStorage.removeItem('trainaxis_club_slug');
    localStorage.removeItem('trainaxis_calendar');
    // Full page reload to /login clears all React state — TenantContext, ThemeContext, etc.
    window.location.href = '/login';
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const next = { ...prev, ...updates };
      localStorage.setItem('trainaxis_user', JSON.stringify(next));
      return next;
    });
  }, []);

  const getDashboardPath = useCallback(
    (role) => ROLE_DASHBOARDS[role] || '/login',
    []
  );

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, getDashboardPath }}>
      {children}
    </AuthContext.Provider>
  );
}

